import {
  isContactImportJobExecutionStatus,
  isContactImportJobPreviewStatus,
  isContactImportJobTerminalStatus,
} from "@multi-chat/shared";

export type ContactImportDialogView = "loading" | "setup" | "map" | "progress" | "summary";

export type ContactImportDialogViewInput = {
  jobStatus: string | null;
  jobId?: string | null;
  restoreJobId?: string | null;
  activeJobCheckPending: boolean;
  previewPending: boolean;
};

/**
 * Dialog body to render. Preview/upload states never reuse the processing view.
 * Terminal jobs use a dedicated summary, not the in-progress panel.
 */
export function contactImportDialogView(
  input: ContactImportDialogViewInput,
): ContactImportDialogView {
  if (input.restoreJobId && input.jobId !== input.restoreJobId) {
    return "loading";
  }
  const status = input.jobStatus;
  if (status != null && isContactImportJobTerminalStatus(status)) {
    return "summary";
  }
  if (status != null && isContactImportJobExecutionStatus(status)) {
    return "progress";
  }
  if (status === "AWAITING_CONFIRMATION") {
    return "map";
  }
  if (input.activeJobCheckPending || input.previewPending) {
    return "loading";
  }
  return "setup";
}

export function isKnownContactImportDialogJob(
  jobStatus: string | null,
  jobId: string | null,
  restoreJobId?: string | null,
): boolean {
  if (jobStatus == null || jobId == null) return false;
  if (restoreJobId && restoreJobId !== jobId) return false;
  return (
    isContactImportJobExecutionStatus(jobStatus) ||
    isContactImportJobTerminalStatus(jobStatus) ||
    jobStatus === "AWAITING_CONFIRMATION"
  );
}

/**
 * Whether in-memory job state is safe to show before the open-session fetch returns.
 * Toast restore waits for GET /jobs/:id unless the job is already terminal.
 * The Import button never trusts a stale completed job (that belongs to the toast).
 * A job bound in this open session stays trusted through CANCELLED / COMPLETED / FAILED
 * even when `/active` is null.
 */
export function trustInMemoryContactImportDialogJob(
  jobStatus: string | null,
  jobId: string | null,
  restoreJobId?: string | null,
  activeCheckSettled = false,
  boundJobId?: string | null,
): boolean {
  if (jobStatus == null || jobId == null) return false;
  if (restoreJobId) {
    if (restoreJobId !== jobId) return false;
    if (isContactImportJobTerminalStatus(jobStatus)) return true;
    return activeCheckSettled && isContactImportJobExecutionStatus(jobStatus);
  }
  if (boundJobId === jobId) {
    return (
      isContactImportJobExecutionStatus(jobStatus) ||
      isContactImportJobTerminalStatus(jobStatus) ||
      jobStatus === "AWAITING_CONFIRMATION"
    );
  }
  return (
    isContactImportJobExecutionStatus(jobStatus) || jobStatus === "AWAITING_CONFIRMATION"
  );
}

export type ContactImportDialogDismissAction = "keep-running" | "abandon-draft" | "reset";

/**
 * Closing the dialog keeps an import that is already being processed.
 * An unconfirmed upload or mapping draft is abandoned. Anything else starts fresh.
 */
export function contactImportDialogDismissAction(
  status: string | null | undefined,
): ContactImportDialogDismissAction {
  if (status != null && isContactImportJobExecutionStatus(status)) return "keep-running";
  if (status != null && isContactImportJobPreviewStatus(status)) return "abandon-draft";
  return "reset";
}

export function shouldShowContactImportJobCancel(
  dialogView: ContactImportDialogView,
  jobStatus?: string | null,
): boolean {
  return dialogView === "progress" && jobStatus !== "CANCEL_REQUESTED";
}

export function contactImportFlowStep(
  dialogView: ContactImportDialogView,
): 1 | 2 | 3 {
  if (dialogView === "map") return 2;
  if (dialogView === "progress" || dialogView === "summary") return 3;
  return 1;
}

export function decideContactImportOpenSession<T extends { id: string }>(input: {
  restoreJobId: string | null;
  restoredJob: T | null;
  activeJob: T | null;
}): { action: "apply"; job: T } | { action: "reset" } | { action: "wait" } {
  if (input.restoreJobId) {
    if (input.restoredJob?.id === input.restoreJobId) {
      return { action: "apply", job: input.restoredJob };
    }
    if (input.activeJob?.id === input.restoreJobId) {
      return { action: "apply", job: input.activeJob };
    }
    return { action: "wait" };
  }
  if (input.activeJob) {
    return { action: "apply", job: input.activeJob };
  }
  return { action: "reset" };
}
